<template>
  <form
    v-if="isPermittedAll([
      'CustomersGetByID'
    ])"
    id="customer-form-form"
    @submit.prevent="saveCustomer">
    <v-container
      class="h-100 d-flex flex-column pt-0">
      <!-- Title -->
      <v-row
        class="flex-grow-0">
        <v-col class="py-0">
          <h3>
            <span
              class="mr-1"
              v-if="$store.getters.isActiveCustomerDirty || supportContractEndUtc">
              *
            </span>
            <span v-if="activeCustomer.CustomerID">
              Edit Customer #{{activeCustomer.CustomerID}}
              -
              {{activeCustomerCopy.CustomerName}}
              <span
                class="customer-status-dot mt-3"
                :class="activeCustomerCopy.CustomerStatusColor.CustomerStatusColorName">
              </span>
            </span>
            <span v-else>Add Customer</span>
          </h3>
        </v-col>
      </v-row>
      <!-- / Title -->

        <v-row
          class="flex-grow-0">
          <v-col :cols="isMobile ? 12 : 8">
            <v-row>
              <v-col>
                <v-text-field
                  id="customer-form-name"
                  name="customerName"
                  v-model="activeCustomer.CustomerName"
                  label="Customer:"
                  :readonly="$store.getters.isLoadingActive"
                  hide-details>
                </v-text-field>
              </v-col>
            </v-row>     
            <v-row>
              <v-col :cols="isMobile ? 12 : 6">
                <Autocomplete
                  id="customer-form-type"
                  label="Customer Type:"
                  v-model="customerType"
                  :items="$store.state.customerTypes"
                  item-value="CustomerTypeID"
                  item-text="CustomerTypeName">
                </Autocomplete>
              </v-col>
              <v-col :cols="isMobile ? 12 : 6">
                <v-text-field
                  id="customer-form-website"
                  name="customerWebsite"
                  v-model="activeCustomer.CustomerWebsite"
                  label="Website:"
                  :readonly="$store.getters.isLoadingActive"
                  hide-details>
                </v-text-field>
              </v-col>
            </v-row>
            <v-row           
              v-if="activeCustomer.CustomerID
                && isPermittedAll([
                  'EmployeesGetAll'
                ])">
              <v-col :cols="isMobile ? 12 : 6">
                <Autocomplete
                  id="customer-form-shipping-contact"
                  label="Supervisor:"
                  v-model="shippingContact"
                  :items="$store.state.customerEmployeesForDropdown"
                  item-value="EmployeeID"
                  item-text="EmployeeName">
                </Autocomplete>
              </v-col>    
            </v-row>
          </v-col>      
          <v-col :cols="isMobile ? 12 : 4">
            <v-row>
              <v-col
                v-if="isPermittedAll([
                  'CustomersSetCustomerIsDealer',
                ])"
                class="d-flex align-center">
                <v-checkbox
                  id="customer-form-customer-is-dealer"
                  label="Customer is a dealer."
                  v-model="activeCustomer.CustomerIsDealer"
                  :readonly="$store.getters.isLoadingActive"
                  :class="isMobile ? 'mt-0' : 'mt-6'">
                </v-checkbox>
                <span
                  class="customer-status-dot blue"
                  :class="isMobile ? '' : 'mt-5'">
                </span>
              </v-col>
            </v-row>
            <v-row>
              <v-col
                v-if="isPermittedAll([
                  'CustomersSetSupportRenewalDeclined',
                ])"
                class="d-flex align-center">
                <v-checkbox
                  id="customer-form-renewal-declined"
                  label="Customer has declined support."
                  v-model="activeCustomer.SupportRenewalDeclined"
                  :readonly="$store.getters.isLoadingActive"
                  :class="isMobile ? 'mt-0' : 'mt-6'">
                </v-checkbox>
                <span
                  class="customer-status-dot red"
                  :class="isMobile ? '' : 'mt-5'">
                </span>
              </v-col>
            </v-row> 
            <v-row>
              <v-col
                v-if="isPermittedAll([
                  'CustomersSetCustomerIsTrial',
                ])"
                class="d-flex align-center">
                <v-checkbox
                  id="customer-form-trial-customer"
                  label="Customer is a trial."
                  v-model="activeCustomer.CustomerIsTrial"
                  :readonly="$store.getters.isLoadingActive"
                  :class="isMobile ? 'mt-0' : 'mt-6'">
                </v-checkbox>
                <span
                  class="customer-status-dot purple"
                  :class="isMobile ? '' : 'mt-5'">
                </span>
              </v-col>
            </v-row>         
          </v-col>
        </v-row>

      <!-- Shipping / Billing Addresses & Notes -->
      <v-row
        class="flex-grow-0">
        <!-- Shipping -->
        <v-col :cols="isMobile ? 12 : 'default'">
          <!-- Shipping Label -->
          <v-row>
            <v-col class="py-0">
              <h4>Shipping Information</h4>
            </v-col>
          </v-row>
          <!-- / Shipping Label -->

          <!-- Shipping Address 1 -->
          <v-row>
            <v-col>
              <v-text-field
                id="customer-shipping-contact-address1"
                name="customerShippingContactAddress1"
                v-model="activeCustomer.CustomerAddress1"
                label="Address 1:"
                :readonly="$store.getters.isLoadingActive"
                hide-details>
              </v-text-field>
            </v-col>
          </v-row>
          <!-- / Shipping Address 1 -->

          <!-- Shipping Address 2 -->
          <v-row>
            <v-col>
              <v-text-field
                id="customer-shipping-contact-address2"
                name="customerShippingContactAddress2"
                v-model="activeCustomer.CustomerAddress2"
                label="Address 2:"
                :readonly="$store.getters.isLoadingActive"
                hide-details>
              </v-text-field>
            </v-col>
          </v-row>
          <!-- / Shipping Address 2 -->

          <!-- Shipping City/State/Zip -->
          <v-row>
            <v-col
              :cols="isMobile ? 12 : 6">
              <v-text-field
                id="customer-shipping-city"
                name="customerShippingCity"
                v-model="activeCustomer.CustomerCity"
                label="City:"
                :readonly="$store.getters.isLoadingActive"
                hide-details>
              </v-text-field>
            </v-col>
            <v-col
              :cols="isMobile ? 6 : 3">
              <v-text-field
                id="customer-shipping-state"
                name="customerShippingState"
                v-model="activeCustomer.CustomerState"
                label="State:"
                :readonly="$store.getters.isLoadingActive"
                hide-details>
              </v-text-field>
            </v-col>
            <v-col
              :cols="isMobile ? 6 : 3">
              <v-text-field
                id="customer-shipping-zip"
                name="customerShippingZip"
                v-model="activeCustomer.CustomerZipCode"
                label="Zip:"
                :readonly="$store.getters.isLoadingActive"
                hide-details>
              </v-text-field>
            </v-col>
          </v-row>
          <!-- / Shipping City/State/Zip -->

          <!-- Shipping Phone 1/2 -->
          <v-row>
            <v-col>
              <v-text-field
                id="customer-shipping-contact-phone1"
                name="customerShippingContactPhone1"
                v-model="activeCustomer.CustomerPhone1"
                label="Phone 1:"
                :readonly="$store.getters.isLoadingActive"
                hide-details>
              </v-text-field>
            </v-col>
            <v-col>
              <v-text-field
                id="customer-shipping-contact-phone2"
                name="customerShippingContactPhone2"
                v-model="activeCustomer.CustomerPhone2"
                label="Phone 2:"
                :readonly="$store.getters.isLoadingActive"
                hide-details>
              </v-text-field>
            </v-col>
          </v-row>
          <!-- / Shipping Phone 1/2 -->

          <!-- Shipping Fax -->
          <v-row>
            <v-col cols="6">
              <v-text-field
                id="customer-shipping-contact-fax"
                name="customerShippingContactFax"
                v-model="activeCustomer.CustomerFax"
                label="Fax:"
                :readonly="$store.getters.isLoadingActive"
                hide-details>
              </v-text-field>
            </v-col>
          </v-row>
          <!-- / Shipping Fax -->

          <!-- Shipping Email -->
          <v-row>
            <v-col>
              <v-text-field
                id="customer-shipping-contact-email"
                name="customerShippingContactEmail"
                v-model="activeCustomer.CustomerEmail"
                label="Email:"
                :readonly="$store.getters.isLoadingActive"
                hide-details>
              </v-text-field>
            </v-col>
          </v-row>
          <!-- / Shipping Email -->
        </v-col>
        <!-- / Shipping -->

        <!-- Billing -->
        <v-col :cols="isMobile ? 12 : 'default'">
          <!-- Billing Label -->
          <v-row>
            <v-col class="py-0">
              <h4>Billing Information</h4>
            </v-col>
          </v-row>
          <!-- / Billing Label -->

          <!-- Billing Address 1 -->
          <v-row>
            <v-col>
              <v-text-field
                id="customer-billing-contact-address1"
                name="customerBillingContactAddress1"
                v-model="activeCustomer.BillingAddress1"
                label="Address 1:"
                :readonly="$store.getters.isLoadingActive"
                hide-details>
              </v-text-field>
            </v-col>
          </v-row>
          <!-- / Billing Address 1 -->

          <!-- Billing Address 2 -->
          <v-row>
            <v-col>
              <v-text-field
                id="customer-billing-contact-address2"
                name="customerBillingContactAddress2"
                v-model="activeCustomer.BillingAddress2"
                label="Address 2:"
                :readonly="$store.getters.isLoadingActive"
                hide-details>
              </v-text-field>
            </v-col>
          </v-row>
          <!-- / Billing Address 2 -->

          <!-- Billing City/State/Zip -->
          <v-row>
            <v-col
              :cols="isMobile ? 12 : 6">
              <v-text-field
                id="customer-billing-city"
                name="customerBillingCity"
                v-model="activeCustomer.BillingCity"
                label="City:"
                :readonly="$store.getters.isLoadingActive"
                hide-details>
              </v-text-field>
            </v-col>
            <v-col
              :cols="isMobile ? 6 : 3">
              <v-text-field
                id="customer-billing-state"
                name="customerBillingState"
                v-model="activeCustomer.BillingState"
                label="State:"
                :readonly="$store.getters.isLoadingActive"
                hide-details>
              </v-text-field>
            </v-col>
            <v-col
             :cols="isMobile ? 6 : 3">
              <v-text-field
                id="customer-billing-zip"
                name="customerBillingZip"
                v-model="activeCustomer.BillingZipCode"
                label="Zip:"
                :readonly="$store.getters.isLoadingActive"
                hide-details>
              </v-text-field>
            </v-col>
          </v-row>
          <!-- / Billing City/State/Zip -->

          <!-- Billing Contact -->
          <v-row
            v-if="activeCustomer.CustomerID
              && isPermittedAll([
                'EmployeesGetAll'
              ])">
            <v-col>
              <Autocomplete
                id="customer-form-billing-contact"
                label="Contact:"
                v-model="billingContact"
                :items="$store.state.customerEmployeesForDropdown"
                item-value="EmployeeID"
                item-text="EmployeeName">
              </Autocomplete>
            </v-col>
          </v-row>
          <!-- / Billing Contact -->

          <!-- Billing Phone 1/2 -->
          <v-row
            v-if="activeCustomer.CustomerID">
            <v-col>
              <v-text-field
                id="customer-billing-contact-phone1"
                name="customerBillingContactPhone1"
                :value="activeCustomer.BillingContactEmployee
                  && activeCustomer.BillingContactEmployee.EmployeePhone1"
                label="Phone 1:"
                disabled
                hide-details>
              </v-text-field>
            </v-col>
            <v-col>
              <v-text-field
                id="customer-billing-contact-phone2"
                name="customerBillingContactPhone2"
                :value="activeCustomer.BillingContactEmployee
                  && activeCustomer.BillingContactEmployee.EmployeePhone2"
                label="Phone 2:"
                disabled
                hide-details>
              </v-text-field>
            </v-col>
          </v-row>
          <!-- / Billing Phone 1/2 -->

          <!-- Billing Fax -->
          <v-row
            v-if="activeCustomer.CustomerID">
            <v-col cols="6">
              <v-text-field
                id="customer-billing-contact-fax"
                name="customerBillingContactFax"
                :value="activeCustomer.BillingContactEmployee
                  && activeCustomer.BillingContactEmployee.EmployeeFax"
                label="Fax:"
                disabled
                hide-details>
              </v-text-field>
            </v-col>
          </v-row>
          <!-- / Billing Fax -->

          <!-- Billing Email -->
          <v-row
            v-if="activeCustomer.CustomerID">
            <v-col>
              <v-text-field
                id="customer-billing-contact-email"
                name="customerBillingContactEmail"
                :value="activeCustomer.BillingContactEmployee
                  && activeCustomer.BillingContactEmployee.EmployeeEmail"
                label="Email:"
                hide-details
                disabled>
              </v-text-field>
            </v-col>
          </v-row>
          <!-- / Billing Email -->
        </v-col>
        <!-- / Billing -->

        <!-- Call History -->
        <v-col :cols="isMobile ? 12 : 'default'"
          id="customer-history-container">
          <label
            for="customer-form-history">
            Call History:
          </label>
          <v-textarea
            id="customer-form-history"
            class="flex-grow-1 pt-0 mt-0 h-100"
            name="customerNotes"
            :value="callHistory"
            readonly
            outlined
            filled
            hide-details
            no-resize>
          </v-textarea>
        </v-col>
        <!-- / Call History -->
      </v-row>

        <!-- Notes -->
        <v-row>
        <v-col class="d-flex flex-column pb-3 mt-3" :cols="isMobile ? 12 : 6">
          <label
            for="customer-form-notes">
            Notes:
          </label>
          <v-textarea
            id="customer-form-notes"
            class="flex-grow-1 pt-0 mt-0"
            name="customerNotes"
            v-model="activeCustomer.CustomerNotes"
            :readonly="$store.getters.isLoadingActive"
            hide-details
            no-resize>
          </v-textarea>
        </v-col>
        <!-- / Notes -->
        </v-row>


      <!-- / Shipping / Billing Addresses & Notes -->

      <!-- Employees -->
      <v-row
        v-if="activeCustomer.CustomerID
          && isPermittedAll([
            'EmployeesGetAll'
          ])"
        class="flex-grow-0">
        <v-col
          v-if="$route.params.CustomerID !== undefined">
          <CustomerEmployeesFormTable
            :customer-id="$route.params.CustomerID"/>
        </v-col>
        <v-col
          v-else>
          <CustomerEmployeesFormTable
            :customer-id="activeCustomer.CustomerID"/>
        </v-col>
      </v-row>
      <!-- / Employees -->

      <!-- Computers -->
      <v-row
        v-if="activeCustomer.CustomerID
          && isPermittedAll([
            'ComputersGetAll'
          ])"
        class="flex-grow-0">
        <v-col
          v-if="$route.params.CustomerID !== undefined">
          <CustomerComputersFormTable
            :customer-id="$route.params.CustomerID"
            :hide-add-button="true"
          />
        </v-col>
        <v-col
          v-else>
          <CustomerComputersFormTable
            :customer-id="activeCustomer.CustomerID"
            :hide-add-button="true"
          />
        </v-col>
      </v-row>
      <!-- / Computers -->

      <!-- Support Date / Update Support -->
      <v-row
        v-if="activeCustomer.CustomerID
          && isPermittedAll([
            'SupportContractsUpdate'
          ])">
        <v-col :cols="isMobile ? 6 : 4">
          <DatePicker
            input-id="computer-form-support-date"
            v-model="supportContractEndUtc"
            label="Support End Date:"
            :line-bottom="true"
            :readonly="$store.getters.isLoadingActive"
          />
          <small
            class="error--text font-weight-bold">
            This will update all computers for this customer.
          </small>
        </v-col>
        <v-col
          cols="auto">
          <v-btn
            class="mt-6"
            type="button"
            color="primary"
            :disabled="$store.getters.isLoadingActive
              || !supportContractEndUtc"
            @click="updateSupport">
            Update Support
          </v-btn>
        </v-col>
      </v-row>
      <!-- / Support Date / Update Support -->

      <v-spacer></v-spacer>

      <!-- Form Buttons -->
       <v-row>
        <v-col class="d-flex justify-end">
          <LastModifiedBy
            class="order-first"
            :entity="activeCustomer"
          />
        </v-col>
       </v-row>
      <v-row
        class="flex-grow-0">

        <v-col class="d-flex align-center" >
          <v-btn
            v-if="isPermittedToSave"
            type="submit"
            class="ml-auto"
            color="primary"
            :disabled="$store.getters.isLoadingActive
              || !$store.getters.isActiveCustomerDirty">
            Save
          </v-btn>

          <v-btn
            @click="goToCustomersTable"
            type="button"
            color="primary"
            class="order-first"
            outlined>
            Back
          </v-btn>

          <v-btn
            v-if="activeCustomer.CustomerID
              && isPermittedAll([
                'CustomersDelete',
              ])
              && !activeCustomer.CustomerIsDeleted"
            type="button"
            color="error"
            class="order-first ml-5"
            @click="hideCustomer"
            :disabled="$store.getters.isLoadingActive"
            outlined>
            Hide Customer
          </v-btn>

          <v-btn
            v-if="activeCustomer.CustomerID
              && isPermittedAll([
                'CustomersRestore',
              ])
              && activeCustomer.CustomerIsDeleted"
            type="button"
            color="error"
            class="order-first mr-5"
            outlined
            @click="restoreCustomer"
            :disabled="$store.getters.isLoadingActive">
            Unhide Customer
          </v-btn>


        </v-col>

      </v-row>
      <!-- / Form Buttons -->
    </v-container>
  </form>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import CustomerComputersFormTable from './CustomerComputersFormTable.vue';
import CustomerEmployeesFormTable from './CustomerEmployeesFormTable.vue';
import DatePicker from './DatePicker.vue';
import LastModifiedBy from './LastModifiedBy.vue';

export default {
  name: 'CustomerForm',
  components: {
    CustomerComputersFormTable,
    CustomerEmployeesFormTable,
    DatePicker,
    LastModifiedBy,
  },
  data() {
    return {
      supportContractEndUtc: undefined,
    };
  },
  computed: {
    isPermittedToSave() {
      return (!this.activeCustomer.CustomerID
          && this.isPermittedAll(['CustomersCreate']))
        || (this.activeCustomer.CustomerID
          && this.isPermittedAll(['CustomersUpdate']));
    },
    activeCustomer() {
      return this.$store.state.activeCustomer;
    },
    activeCustomerCopy() {
      return this.$store.state.activeCustomerCopy;
    },
    customerType: {
      get() {
        return this.activeCustomer.CustomerType;
      },
      set(customerType) {
        if (customerType) {
          this.activeCustomer.CustomerType = _.cloneDeep(customerType);
          this.activeCustomer.CustomerTypeID = customerType.CustomerTypeID;
        }
      },
    },
    shippingContact: {
      get() {
        return this.activeCustomer.SupervisorEmployee;
      },
      set(shippingContact) {
        if (shippingContact) {
          this.activeCustomer.SupervisorEmployee = _.cloneDeep(shippingContact);
          this.activeCustomer.SupervisorEmployeeID = shippingContact.EmployeeID;
        }
      },
    },
    billingContact: {
      get() {
        return this.activeCustomer.BillingContactEmployee;
      },
      set(billingContact) {
        if (billingContact) {
          this.activeCustomer.BillingContactEmployee = _.cloneDeep(billingContact);
          this.activeCustomer.BillingContactEmployeeID = billingContact.EmployeeID;
        }
      },
    },
    customerEmployees() {
      return this.$store.state.customerEmployees;
    },
    callHistory() {
      let historyStr = '';

      _.each(this.activeCustomer.CustomerCallHistory, (history) => {
        historyStr += `${moment(history.CallStartUtc).format('M/D/YYYY [at] h:mm A')}\n`;
        historyStr += `Tech: ${history.CreatedBy.TransactUser.UserGivenName}\n`;

        if (history.Employee) {
          historyStr += `Name: ${history.Employee.EmployeeName}\n`;
        }

        if (history.ComputerID) {
          historyStr += `Computer: ${history.Computer.SerialNumber}\n`;
        }

        if (history.CallProblem) {
          historyStr += `${history.CallProblem}\n`;
        }

        if (history.CallNotes) {
          historyStr += `${history.CallNotes}\n`;
        }

        historyStr += '\n';
      });

      return historyStr;
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    }

  },
  methods: {
    saveCustomer() {
      if (this.activeCustomer.CustomerID) {
        this.$store.dispatch('updateCustomer', { resetToCustomer: true });
      } else {
        this.$store.dispatch('createCustomer', { resetToCustomer: true });
      }
    },
    hideCustomer() {
      const confirm = () => {
        this.$store.dispatch('deleteCustomer')
          .then(() => {
            this.goToCustomersTable();
          });
      };

      this.showConfirm(
        'Are you sure you want to hide this customer?',
        'Hide Customer',
      ).then(confirm, _.noop);
    },
    updateSupport() {
      this.$store.dispatch(
        'updateAllSupportContracts',
        {
          supportContractEndUtc: this.supportContractEndUtc,
        },
      ).then(() => {
        this.supportContractEndUtc = undefined;
      });
    },
    restoreCustomer() {
      const confirm = () => {
        this.$store.dispatch('restoreCustomer')
          .then(() => {
            this.goToCustomersTable();
          });
      };

      this.showConfirm(
        'Are you sure you want to unhide this customer?',
        'Hide Customer',
      ).then(confirm, _.noop);
    },
  },
  created() {
    if (this.$route.params.CustomerID) {
      this.$store.dispatch('getCustomer', this.$route);
      this.$store.dispatch('getCustomerEmployeesForDropdown', this.$route);
    }

    this.$store.dispatch('getCustomerTypes');
  },
};
</script>

<style lang="scss">
#customer-form-form {
  overflow-y: auto;

  #customer-history-container {
    fieldset {
      border: none;
    }

    .v-input__slot {
      padding: 0;

      &:hover {
        background-color: rgba(255, 255, 255, 0.08);
      }
    }

    textarea {
      margin-top: 0;
      padding: 4px 8px;
    }
  }
}

.v-application.theme--light {
  #customer-form-form  {
    #customer-history-container {
      .v-input__slot {
        background-color: #eeeeee !important;
      }
    }
  }
}

.v-application.theme--dark {
  #customer-form-form  {
    #customer-history-container {
      .v-input__slot {
        textarea {
          color: rgba(255, 255, 255, 0.5);
        }
      }
    }
  }
}
</style>
